import {
  Box,
  Button,
  Flex,
  H4,
  MaterialTableWrapper,
  MaterialTableWrapperProps,
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableOptions,
  OusterProvider,
  Tooltip,
  withThemeProvider,
} from '@ouster/webviz-react-components';

import { useMemo } from 'react';
import { MergerRecord } from 'src/components/adminDashboard/types';
import { useStoreState } from 'src/reducers';
import { isAdminLevelRole } from 'src/utils/roles';

import { formatDateLong } from '../utils';
import { ActiveMergerAction, MERGER_ACTION, STATUS_STYLE_MAP } from './const';

interface ActiveRequestsProps {
  data: MergerRecord[];
  onOpenMerger: (mergerId: number, mode: 'view' | 'edit') => void;
  onMergeMerger: (mergerRecord: MergerRecord) => void;
  onResendMergerEmail: (mergerRecord: MergerRecord) => void;
  onDeleteMerger: (mergerRecord: MergerRecord) => void;
  activeMergerAction: ActiveMergerAction | null;
}

const ThemedMaterialTableWrapper =
  withThemeProvider<MaterialTableWrapperProps<MergerRecord>>(
    MaterialTableWrapper
  );

type SourceApproval = {
  org_id: number;
  approved: boolean;
};

const TOOLTIP_LABEL = {
  NO_PERMISSION: 'Admin or initiator access required.',
  MUST_BE_FINALIZED: 'Cannot be a draft request.',
  NEED_ALL_APPROVALS: 'All approvals required.',
  NO_RESEND_NEEDED: 'All approvals received. No reminder needed.',
} as const;

const getSourceApprovals = (mergerRecord: MergerRecord): SourceApproval[] => {
  const maybeSourceApprovals = (mergerRecord as { source_approvals?: unknown })
    .source_approvals;

  if (!Array.isArray(maybeSourceApprovals)) {
    return [];
  }

  return maybeSourceApprovals.filter(
    (sourceApproval): sourceApproval is SourceApproval => {
      if (typeof sourceApproval !== 'object' || sourceApproval === null) {
        return false;
      }

      const candidate = sourceApproval as {
        org_id?: unknown;
        approved?: unknown;
      };

      return (
        typeof candidate.org_id === 'number' &&
        typeof candidate.approved === 'boolean'
      );
    }
  );
};

const getApprovedSourceCount = (mergerRecord: MergerRecord): number =>
  getSourceApprovals(mergerRecord).filter(
    (sourceApproval) => sourceApproval.approved
  ).length;

const hasAllApprovals = (mergerRecord: MergerRecord): boolean => {
  if (!mergerRecord.target_approval) {
    return false;
  }

  const approvedSourceOrgIds = new Set(
    getSourceApprovals(mergerRecord)
      .filter((sourceApproval) => sourceApproval.approved)
      .map((sourceApproval) => sourceApproval.org_id)
  );

  return mergerRecord.source_orgs.every((sourceOrgId) =>
    approvedSourceOrgIds.has(Number(sourceOrgId))
  );
};

const renderMergedOrgCountCell: MRT_ColumnDef<MergerRecord>['Cell'] = ({
  cell,
}) => (
  <Box
    as="span"
    color="gray.200"
    bgColor="gray.700"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    height="20px"
    px={2}
    rounded={4}
    fontSize={12}
    lineHeight={1}
  >
    {`${cell.getValue<number[]>().length} sources`}
  </Box>
);

const renderStatusCell: MRT_ColumnDef<MergerRecord>['Cell'] = ({ row }) => {
  const mergerRecord = row.original;
  const statusStyle = STATUS_STYLE_MAP[mergerRecord.status];
  const totalRequiredApprovals = mergerRecord.source_orgs.length + 1;
  const signedApprovals =
    (mergerRecord.target_approval ? 1 : 0) +
    getApprovedSourceCount(mergerRecord);
  const showApprovalProgress =
    mergerRecord.status === 'PENDING' || mergerRecord.status === 'EXECUTING';

  return (
    <Flex alignItems="center" gap={2} fontSize={12}>
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        height="20px"
        px={2}
        rounded={4}
        lineHeight={1}
        minW="80px"
        bg={statusStyle.bg}
        color={statusStyle.color}
      >
        {statusStyle.label}
      </Box>
      {showApprovalProgress ? (
        <Box as="span" color="gray.400">
          {`${signedApprovals}/${totalRequiredApprovals} Signed`}
        </Box>
      ) : null}
    </Flex>
  );
};

const RowActions = ({
  row,
  onOpenMerger,
  onMergeMerger,
  onResendMergerEmail,
  onDeleteMerger,
  activeMergerAction,
}: {
  row: MRT_Row<MergerRecord>;
  onOpenMerger: (mergerId: number, mode: 'view' | 'edit') => void;
  onMergeMerger: (mergerRecord: MergerRecord) => void;
  onResendMergerEmail: (mergerRecord: MergerRecord) => void;
  onDeleteMerger: (mergerRecord: MergerRecord) => void;
  activeMergerAction: ActiveMergerAction | null;
}) => {
  const { appUser, curUserOrg } = useStoreState(
    (storeState) => storeState.user
  );

  const mergerRecord: MergerRecord = row.original;

  const isAdminLevelUser = isAdminLevelRole(curUserOrg?.role);
  const isSupportInitiator =
    curUserOrg?.role === 'SUPPORT' && mergerRecord.preparer_id === appUser?.id;
  const hasMergerEditPermission = isAdminLevelUser || isSupportInitiator;
  const allApprovalsComplete = hasAllApprovals(mergerRecord);

  const isEditDisabled =
    !hasMergerEditPermission || mergerRecord.status !== 'DRAFT';

  const isMergeDisabled =
    !hasMergerEditPermission ||
    mergerRecord.status !== 'PENDING' ||
    !allApprovalsComplete;
  const isResendEmailDisabled =
    !hasMergerEditPermission ||
    mergerRecord.status !== 'PENDING' ||
    allApprovalsComplete;
  const isDeleteDisabled =
    !hasMergerEditPermission ||
    (mergerRecord.status !== 'DRAFT' && mergerRecord.status !== 'PENDING');

  const isOpenLoading =
    activeMergerAction?.mergerId === mergerRecord.merger_id &&
    activeMergerAction.action === MERGER_ACTION.OPEN;
  const isMergeLoading =
    activeMergerAction?.mergerId === mergerRecord.merger_id &&
    activeMergerAction.action === MERGER_ACTION.MERGE;
  const isResendLoading =
    activeMergerAction?.mergerId === mergerRecord.merger_id &&
    activeMergerAction.action === MERGER_ACTION.RESEND;
  const isDeleteLoading =
    activeMergerAction?.mergerId === mergerRecord.merger_id &&
    activeMergerAction.action === MERGER_ACTION.DELETE;

  const mergeTooltipLabel = (() => {
    if (isMergeLoading || !isMergeDisabled) {
      return undefined;
    }

    if (!hasMergerEditPermission) {
      return TOOLTIP_LABEL.NO_PERMISSION;
    }

    if (mergerRecord.status === 'DRAFT') {
      return TOOLTIP_LABEL.MUST_BE_FINALIZED;
    }

    if (!allApprovalsComplete) {
      return TOOLTIP_LABEL.NEED_ALL_APPROVALS;
    }

    return undefined;
  })();

  const resendTooltipLabel = (() => {
    if (isResendLoading || !isResendEmailDisabled) {
      return undefined;
    }

    if (!hasMergerEditPermission) {
      return TOOLTIP_LABEL.NO_PERMISSION;
    }

    if (mergerRecord.status !== 'PENDING') {
      return TOOLTIP_LABEL.MUST_BE_FINALIZED;
    }

    if (allApprovalsComplete) {
      return TOOLTIP_LABEL.NO_RESEND_NEEDED;
    }

    return undefined;
  })();

  const deleteTooltipLabel = (() => {
    if (isDeleteLoading || !isDeleteDisabled) {
      return undefined;
    }

    if (!hasMergerEditPermission) {
      return TOOLTIP_LABEL.NO_PERMISSION;
    }

    return undefined;
  })();

  return (
    <OusterProvider>
      <Flex gap={2} alignItems="center" wrap="wrap" w="max-content">
        {isEditDisabled ? (
          <Button
            colorScheme="gray"
            size="sm"
            onClick={() => onOpenMerger(mergerRecord.merger_id, 'view')}
            disabled={isOpenLoading}
            style={isOpenLoading ? { opacity: 0.7 } : {}}
            minW="50px"
          >
            {isOpenLoading ? 'Loading...' : 'View'}
          </Button>
        ) : (
          <Button
            colorScheme="gray"
            size="sm"
            onClick={() => onOpenMerger(mergerRecord.merger_id, 'edit')}
            disabled={isEditDisabled || isOpenLoading}
            style={isEditDisabled || isOpenLoading ? { opacity: 0.5 } : {}}
            minW="50px"
          >
            {isOpenLoading ? 'Loading...' : 'Edit'}
          </Button>
        )}
        <Tooltip label={mergeTooltipLabel}>
          <span>
            <Button
              colorScheme="gray"
              size="sm"
              onClick={() => onMergeMerger(mergerRecord)}
              disabled={isMergeDisabled || isMergeLoading}
              style={isMergeDisabled || isMergeLoading ? { opacity: 0.5 } : {}}
            >
              {isMergeLoading ? 'Merging...' : 'Merge'}
            </Button>
          </span>
        </Tooltip>
        <Tooltip label={resendTooltipLabel}>
          <span>
            <Button
              colorScheme="gray"
              size="sm"
              onClick={() => onResendMergerEmail(mergerRecord)}
              disabled={isResendEmailDisabled || isResendLoading}
              style={
                isResendEmailDisabled || isResendLoading ? { opacity: 0.5 } : {}
              }
            >
              {isResendLoading ? 'Sending...' : 'Resend Email'}
            </Button>
          </span>
        </Tooltip>
        <Tooltip label={deleteTooltipLabel}>
          <span>
            <Button
              colorScheme="gray"
              size="sm"
              onClick={() => onDeleteMerger(mergerRecord)}
              disabled={isDeleteDisabled || isDeleteLoading}
              style={
                isDeleteDisabled || isDeleteLoading ? { opacity: 0.5 } : {}
              }
            >
              {isDeleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </span>
        </Tooltip>
      </Flex>
    </OusterProvider>
  );
};

export default function ActiveRequests({
  data,
  onOpenMerger,
  onMergeMerger,
  onResendMergerEmail,
  onDeleteMerger,
  activeMergerAction,
}: ActiveRequestsProps) {
  const columns = useMemo<MRT_ColumnDef<MergerRecord>[]>(
    () => [
      {
        accessorKey: 'target_org_name',
        header: 'Survivor Org',
      },
      {
        accessorKey: 'source_orgs',
        header: 'Merged Org number',
        Cell: renderMergedOrgCountCell,
      },
      {
        accessorKey: 'preparer_name',
        header: 'Prepared By',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Cell: renderStatusCell,
      },
      {
        accessorKey: 'created_at',
        header: 'Created Date',
        Cell: ({ cell }) => formatDateLong(cell.getValue<string>()),
      },
    ],
    []
  );

  const tableOptions: MRT_TableOptions<MergerRecord> = {
    columns,
    data,
    initialState: {
      pagination: { pageSize: 10, pageIndex: 0 },
      columnPinning: {
        right: ['mrt-row-actions'],
        left: ['merger_id', 'target_org_name'],
      },
      showGlobalFilter: true,
    },
    enableRowActions: true,
    positionGlobalFilter: 'right',
    enableSorting: true,
    enableToolbarInternalActions: false,
    layoutMode: 'grid',
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 350,
        maxSize: 350,
        grow: false,
      },
    },
    getRowId: (originalRow: MergerRecord): string => {
      const mergerId: number = originalRow.merger_id;
      return String(mergerId);
    },
    renderRowActions: ({ row }) => (
      <RowActions
        row={row}
        onOpenMerger={onOpenMerger}
        onMergeMerger={onMergeMerger}
        onResendMergerEmail={onResendMergerEmail}
        onDeleteMerger={onDeleteMerger}
        activeMergerAction={activeMergerAction}
      />
    ),
    muiTablePaperProps: {
      sx: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
      },
    },
    muiTableContainerProps: {
      sx: {
        border: '1px solid',
        borderColor: 'rgba(255, 255, 255, 0.12)',
      },
    },
    muiTableBodyRowProps: {
      hover: false,
    },
  };

  return (
    <Box position="relative">
      <H4
        color="gray.300"
        fontSize={14}
        position="absolute"
        left={0}
        top={4}
        zIndex={2}
        display="flex"
        alignItems="center"
        gap={2}
      >
        <Box
          borderRadius="full"
          w={2}
          h={2}
          bg="yellow.300"
          display="inline-block"
          aria-hidden="true"
        />
        <span style={{ lineHeight: 1 }}>
          Active Merge Requests: {data.length}
        </span>
      </H4>
      <Box zIndex={1}>
        <ThemedMaterialTableWrapper {...tableOptions} />
      </Box>
    </Box>
  );
}
